import React from "react";
import { useNavigate } from "react-router-dom";
import { resolveImg } from "@/lib/api";
import { fmtRent, fmtDate } from "@/lib/constants";
import { CompatibilityPill } from "@/components/CompatibilityRing";
import { SaveButton } from "@/components/SaveButton";
import { MapPin, BedDouble, Users, Wallet } from "lucide-react";

export function ListingCard({ listing, savedIds, onSaveChange }) {
  const navigate = useNavigate();
  const img = resolveImg(listing.images?.[0]) || "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=800";
  const score = listing.compatibility?.score;
  return (
    <div
      data-testid={`listing-card-${listing.listing_id}`}
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      className="group cursor-pointer overflow-hidden rounded-3xl bg-white shadow-card transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={img} alt={listing.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute right-3 top-3">
          <SaveButton itemType="listing" itemId={listing.listing_id} savedIds={savedIds} onChange={onSaveChange} />
        </div>
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="rounded-full bg-ink/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur">
            {listing.room_type === "shared" ? "Shared room" : "Private room"}
          </span>
        </div>
        {score != null && (
          <div className="absolute bottom-3 left-3"><CompatibilityPill score={score} /></div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between">
          <p className="font-heading text-xl font-extrabold text-ink">{fmtRent(listing.rent)}<span className="text-sm font-medium text-ink-soft">/mo</span></p>
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-ink-soft">{listing.flat_config}</span>
        </div>
        {listing.budget_fit?.fits && (
          <span data-testid={`budget-fit-${listing.listing_id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-mint/10 px-2 py-0.5 text-[11px] font-bold text-mint">
            <Wallet className="h-3 w-3" /> Fits your budget
          </span>
        )}
        <h4 className="mt-1 line-clamp-1 font-heading text-base font-bold text-ink">{listing.title}</h4>
        <div className="mt-2 flex items-center gap-1.5 text-sm text-ink-soft">
          <MapPin className="h-4 w-4 text-brand" />
          <span className="line-clamp-1">{listing.locality}, {listing.city}</span>
        </div>
        <div className="mt-3 flex items-center gap-4 border-t border-border pt-3 text-xs text-ink-soft">
          <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" /> {listing.furnishing || "—"}</span>
          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {(listing.occupant_ids?.length || 0)} flatmates</span>
          <span className="ml-auto font-semibold text-ink">Move-in {fmtDate(listing.move_in_date)}</span>
        </div>
      </div>
    </div>
  );
}

export function ListingRailCard({ listing }) {
  const navigate = useNavigate();
  const img = resolveImg(listing.images?.[0]) || "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=600";
  const score = listing.compatibility?.score;
  return (
    <div
      data-testid={`listing-rail-${listing.listing_id}`}
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      className="w-56 shrink-0 cursor-pointer overflow-hidden rounded-3xl bg-white shadow-card"
    >
      <div className="relative aspect-[5/4] overflow-hidden">
        <img src={img} alt={listing.title} className="h-full w-full object-cover" />
        {score != null && <div className="absolute bottom-2 left-2"><CompatibilityPill score={score} /></div>}
      </div>
      <div className="p-3">
        <p className="font-heading text-lg font-extrabold text-ink">{fmtRent(listing.rent)}<span className="text-xs font-medium text-ink-soft">/mo</span></p>
        <p className="line-clamp-1 text-xs text-ink-soft">{listing.locality}, {listing.city}</p>
      </div>
    </div>
  );
}
